import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { catchError, mergeMap } from 'rxjs/operators';
import { environment } from './../environments/environment';

// Use proxy to avoid CORS issues
const DEEZER_BASE = '/api/deezer';

/** Deezer answers rate-limit and other failures with HTTP 200 and an { error } body; treat those as errors. */
export function ensureDeezerOk<T>(data: T): T {
  const error = (data as any)?.error;
  if (error) throw new Error(`Deezer error: ${typeof error === 'string' ? error : (error.message || error.type || 'unknown')}`);
  return data;
}

export interface AlbumDetails {
  id: string;
  release_date: string | null;
  fans: number;
  genre_id: number | null;
  nb_tracks: number;
}

export interface Genre {
  id: number;
  name: string;
  albumCount: number | null; // null = unknown
}

export interface BrowseOption {
  key: string;
  label: string;
}

export interface BrowseOptions {
  languages: BrowseOption[];
  moods: BrowseOption[];
  decades: BrowseOption[];
  subgenres: Record<string, BrowseOption[]>;
}

@Injectable({
  providedIn: 'root'
})
export class MusicDataService {
  constructor(private http: HttpClient) { }

  // Transform Deezer artist to Spotify-like format
  private transformArtist(deezerArtist: any): any {
    // Provide multiple image sizes to match Spotify format (images[0], images[1], etc.)
    const images = [];
    if (deezerArtist.picture_xl) images.push({ url: deezerArtist.picture_xl });
    if (deezerArtist.picture_big) images.push({ url: deezerArtist.picture_big });
    if (deezerArtist.picture_medium) images.push({ url: deezerArtist.picture_medium });
    if (deezerArtist.picture_small) images.push({ url: deezerArtist.picture_small });
    if (images.length === 0) images.push({ url: '' });

    return {
      external_urls: { spotify: deezerArtist.link },
      followers: { total: deezerArtist.nb_fan || 0 },
      genres: deezerArtist.genres?.data?.map((g: any) => g.name) || [],
      href: `${DEEZER_BASE}/artist/${deezerArtist.id}`,
      id: String(deezerArtist.id),
      images: images,
      name: deezerArtist.name,
      popularity: deezerArtist.rank || 0,
      type: 'artist',
      uri: `deezer:artist:${deezerArtist.id}`,
      genre: deezerArtist.genres?.data?.map((g: any) => g.name) || []
    };
  }

  // Transform Deezer album to Spotify-like format
  private transformAlbum(deezerAlbum: any): any {
    // Provide multiple image sizes to match Spotify format (images[0], images[1], etc.)
    const images = [];
    if (deezerAlbum.cover_xl) images.push({ url: deezerAlbum.cover_xl });
    if (deezerAlbum.cover_big) images.push({ url: deezerAlbum.cover_big });
    if (deezerAlbum.cover_medium) images.push({ url: deezerAlbum.cover_medium });
    if (deezerAlbum.cover_small) images.push({ url: deezerAlbum.cover_small });
    if (deezerAlbum.cover) images.push({ url: deezerAlbum.cover });
    if (images.length === 0) images.push({ url: '' });

    return {
      external_urls: { spotify: deezerAlbum.link },
      href: `${DEEZER_BASE}/album/${deezerAlbum.id}`,
      id: String(deezerAlbum.id),
      images: images,
      name: deezerAlbum.title,
      release_date: deezerAlbum.release_date,
      total_tracks: deezerAlbum.nb_tracks || 0,
      type: 'album',
      uri: `deezer:album:${deezerAlbum.id}`,
      label: deezerAlbum.label || '',
      copyright: deezerAlbum.copyrights?.map((c: any) => ({ text: c.text || c })) || [],
      external_ids: { upc: deezerAlbum.upc || '' },
      popularity: deezerAlbum.rank || 0,
      artists: deezerAlbum.artist ? [{ id: String(deezerAlbum.artist.id), name: deezerAlbum.artist.name }] : []
    };
  }

  // Transform Deezer track to Spotify-like format
  private transformTrack(deezerTrack: any): any {
    // Build album images array from Deezer cover URLs
    const albumImages = [];
    if (deezerTrack.album?.cover_xl) albumImages.push({ url: deezerTrack.album.cover_xl });
    if (deezerTrack.album?.cover_big) albumImages.push({ url: deezerTrack.album.cover_big });
    if (deezerTrack.album?.cover_medium) albumImages.push({ url: deezerTrack.album.cover_medium });
    if (deezerTrack.album?.cover_small) albumImages.push({ url: deezerTrack.album.cover_small });
    if (deezerTrack.album?.cover) albumImages.push({ url: deezerTrack.album.cover });
    if (albumImages.length === 0) albumImages.push({ url: '' });

    return {
      id: String(deezerTrack.id),
      name: deezerTrack.title,
      duration_ms: deezerTrack.duration * 1000,
      explicit: deezerTrack.explicit_lyrics || false,
      preview_url: deezerTrack.preview,
      external_urls: { spotify: deezerTrack.link },
      uri: `deezer:track:${deezerTrack.id}`,
      artists: deezerTrack.artist ? [{ id: String(deezerTrack.artist.id), name: deezerTrack.artist.name }] : [],
      album: deezerTrack.album ? {
        id: String(deezerTrack.album.id),
        name: deezerTrack.album.title,
        images: albumImages
      } : { id: '', name: 'Unknown Album', images: [] },
      track_number: deezerTrack.track_position || 1,
      disc_number: deezerTrack.disc_number || 1
    };
  }

  // Genres that have albums, with how many (the server drops genres whose chart is empty)
  getGenres(): Observable<Genre[]> {
    return this.http.get<any>(`${DEEZER_BASE}/genres`).pipe(
      mergeMap(data => of((ensureDeezerOk(data).data || []).map((g: any) => ({ id: g.id, name: g.name, albumCount: g.albumCount ?? null })))),
      catchError(error => {
        console.error('Error in getGenres:', error);
        return throwError(() => error);
      })
    );
  }

  getBrowseOptions(): Observable<BrowseOptions> {
    return this.http.get<BrowseOptions>(`${DEEZER_BASE}/browse-options`).pipe(
      catchError(error => {
        console.error('Error in getBrowseOptions:', error);
        return throwError(() => error);
      })
    );
  }

  // Release date, fans, genre and track count for up to 100 album ids, keyed by id
  getAlbumDetails(ids: string[]): Observable<Record<string, AlbumDetails>> {
    return this.http.get<any>(`${DEEZER_BASE}/albums?ids=${ids.join(',')}`).pipe(
      mergeMap(data => {
        const details: Record<string, AlbumDetails> = {};
        for (const album of data.data || []) {
          details[album.id] = album;
        }
        return of(details);
      }),
      catchError(error => {
        console.error('Error in getAlbumDetails:', error);
        return throwError(() => error);
      })
    );
  }

  private toAlbumItems(data: any): any {
    return { albums: { items: (ensureDeezerOk(data).data || []).map((album: any) => this.transformAlbum(album)) } };
  }

  // Deezer has no "new releases" feed: this is the top albums chart, optionally for one genre (0 = all)
  getTopAlbums(genreId: number = 0): Observable<any> {
    return this.http.get<any>(`${DEEZER_BASE}/chart/albums?genre=${genreId}&limit=100`).pipe(
      mergeMap(data => of(this.toAlbumItems(data))),
      catchError(error => {
        console.error('Error in getTopAlbums:', error);
        return throwError(() => error);
      })
    );
  }

  // Albums behind a curated language or sub-genre source (see api/curated.js)
  getCuratedAlbums(key: string): Observable<any> {
    return this.http.get<any>(`${DEEZER_BASE}/curated/${encodeURIComponent(key)}`).pipe(
      mergeMap(data => of(this.toAlbumItems(data))),
      catchError(error => {
        console.error('Error in getCuratedAlbums:', error);
        return throwError(() => error);
      })
    );
  }

  // Artists similar to the given artist, as Deezer sees it
  getRelatedArtists(artistId: string): Observable<any[]> {
    return this.http.get<any>(`${DEEZER_BASE}/artist/${encodeURIComponent(artistId)}/related`).pipe(
      mergeMap(data => of((ensureDeezerOk(data).data || []).map((artist: any) => this.transformArtist(artist)))),
      catchError(error => {
        console.error('Error in getRelatedArtists:', error);
        return throwError(() => error);
      })
    );
  }

  // Most popular artists. Deezer's artist charts ignore the genre, so for a genre this lists the
  // artists of that genre's top albums, in chart order. 0 = Deezer's global artist chart.
  getTopArtists(genreId: number = 0): Observable<any[]> {
    const url = genreId === 0
      ? `${DEEZER_BASE}/chart/artists?genre=0&limit=100`
      : `${DEEZER_BASE}/chart/albums?genre=${genreId}&limit=100`;
    return this.http.get<any>(url).pipe(
      mergeMap(data => {
        const rows: any[] = ensureDeezerOk(data).data || [];
        const artists = genreId === 0 ? rows : rows.map(album => album.artist).filter(Boolean);
        const seen = new Set<number>();
        const unique = artists.filter(a => !seen.has(a.id) && seen.add(a.id));
        return of(unique.map(artist => this.transformArtist(artist)));
      }),
      catchError(error => {
        console.error('Error in getTopArtists:', error);
        return throwError(() => error);
      })
    );
  }

  getCharts(): Observable<any> {
    // Get full chart data (tracks, albums, artists, playlists, podcasts)
    return this.http.get<any>(`${DEEZER_BASE}/chart`).pipe(
      mergeMap(data => {
        const transformed = {
          tracks: (data.tracks?.data || []).map((track: any) => this.transformTrack(track)),
          albums: (data.albums?.data || []).map((album: any) => this.transformAlbum(album)),
          artists: (data.artists?.data || []).map((artist: any) => this.transformArtist(artist))
        };
        return of(transformed);
      }),
      catchError(error => {
        console.error('Error in getCharts:', error);
        return throwError(() => error);
      })
    );
  }

  getArtistById(id): Observable<any> {
    return this.http.get<any>(`${DEEZER_BASE}/artist/${id}`).pipe(
      mergeMap(data => of(this.transformArtist(data))),
      catchError(error => {
        console.error('Error in getArtistById:', error);
        return throwError(() => error);
      })
    );
  }

  getAlbumsByArtistId(id): Observable<any> {
    return this.http.get<any>(`${DEEZER_BASE}/artist/${id}/albums`).pipe(
      mergeMap(data => {
        const transformed = { items: (data.data || []).map((album: any) => this.transformAlbum(album)) };
        return of(transformed);
      }),
      catchError(error => {
        console.error('Error in getAlbumsByArtistId:', error);
        return throwError(() => error);
      })
    );
  }

  getAlbumById(id): Observable<any> {
    return this.http.get<any>(`${DEEZER_BASE}/album/${id}`).pipe(
      mergeMap(data => {
        const transformed = this.transformAlbum(data);
        transformed.tracks = (data.tracks?.data || []).map((track: any) => this.transformTrack(track));
        transformed.artists = data.artist ? [{ id: String(data.artist.id), name: data.artist.name }] : [];
        return of(transformed);
      }),
      catchError(error => {
        console.error('Error in getAlbumById:', error);
        return throwError(() => error);
      })
    );
  }

  searchTracks(searchString: string): Observable<any> {
    const encodedQuery = encodeURIComponent(searchString);
    return this.http.get<any>(`${DEEZER_BASE}/search/track?q=${encodedQuery}`).pipe(
      mergeMap(data => {
        const transformed = {
          tracks: {
            items: (data.data || []).map((track: any) => this.transformTrack(track))
          }
        };
        return of(transformed);
      }),
      catchError(error => {
        console.error('Error in searchTracks:', error);
        return throwError(() => error);
      })
    );
  }

  searchArtists(searchString: string): Observable<any> {
    const encodedQuery = encodeURIComponent(searchString);
    return this.http.get<any>(`${DEEZER_BASE}/search/artist?q=${encodedQuery}`).pipe(
      mergeMap(data => {
        const transformed = {
          artists: {
            items: (data.data || []).map((artist: any) => this.transformArtist(artist))
          }
        };
        return of(transformed);
      }),
      catchError(error => {
        console.error('Error in searchArtists:', error);
        return throwError(() => error);
      })
    );
  }

  addToFavourites(id: string): Observable<any> {
    return this.http.get<string[]>(`${environment.userAPIBase}/favourites/`).pipe(
      mergeMap(favouritesArray => {
        console.log('Current favourites count:', favouritesArray.length);
        if (favouritesArray.length >= 100) {
          throw new Error('Favourites limit reached (max 100)');
        }
        console.log('Adding song to favourites:', id);
        return this.http.put<any>(`${environment.userAPIBase}/favourites/${id}`, {});
      }),
      catchError(error => {
        console.error('Error in addToFavourites:', error);
        return throwError(() => error);
      })
    );
  }

  removeFromFavourites(id: string): Observable<any> {
    return this.http.delete<any>(`${environment.userAPIBase}/favourites/${id}`);
  }

  getFavourites(page: number = 1, limit: number = 20): Observable<any> {
    return this.http.get<string[]>(`${environment.userAPIBase}/favourites/`).pipe(
      mergeMap(favouritesArray => {
        console.log('Got favourites array from API:', favouritesArray);
        if (favouritesArray && favouritesArray.length > 0) {
          const totalItems = favouritesArray.length;
          const startIndex = (page - 1) * limit;
          const pagedIds = favouritesArray.slice(startIndex, startIndex + limit);

          if (pagedIds.length === 0) {
            return of({ tracks: [], total: totalItems, page: page });
          }

          // Fetch tracks from Deezer by IDs
          const ids = pagedIds.join(',');
          console.log('Fetching Deezer tracks with IDs:', ids);
          return this.http.get<any>(`${DEEZER_BASE}/tracks?ids=${ids}`).pipe(
            mergeMap(deezerData => {
              console.log('Deezer response:', deezerData);
              const tracks = (deezerData.data || []).map((track: any) => this.transformTrack(track));
              console.log('Transformed tracks:', tracks);
              return of({ tracks, total: totalItems, page });
            })
          );
        } else {
          return of({ tracks: [], total: 0, page: page });
        }
      }),
      catchError(error => {
        console.error('Error in getFavourites:', error);
        return throwError(() => error);
      })
    );
  }
}


